<template>
  <form-section
    :title="$t('CampaignForm_Events_SelectEvent_Title')"
    :description="$t('CampaignForm_Events_SelectEvent_Desc')"
    :separator="true"
    :is-changed="model.diff && model.diff.includes('event')"
  >
    <form-item>
      <radio-group inline v-model="event_selector">
        <radio
          :border="true"
          label="server"
          :title="$t('CampaignForm_Events_Event_Server')"
          data-qa="trigger-server-event-option-radio"
        />
        <radio
          :border="true"
          label="inapp"
          :title="$t('CampaignForm_Events_Event_Inapp')"
          data-qa="trigger-in-app-event-option-radio"
        />
        <radio
          v-if="sessionStartEnabled"
          border
          label="session_start"
          :title="$t('CampaignForm_Events_Event_Session_Start')"
          data-qa="trigger-session-start-option-radio"
        />
      </radio-group>
    </form-item>

    <div v-if="event_selector !== 'session_start'">
      <el-row>
        <el-col :span="8">
          <form-item :label="$t('CampaignForm_Events_Event')">
            <el-autocomplete
              :placeholder="$t('CampaignForm_Select_Event')"
              clearable
              v-model="model.event_title"
              :fetch-suggestions="querySearchEvents"
              :select-when-unmatched="true"
              :popper-options="{
                modifiers: [
                  hidePopover,
                  'offset',
                  'keepTogether',
                  'arrow',
                  'applyStyle'
                ]
              }"
              style="width: 100%"
              @select="handleSelectEvents"
              @blur="onBlur"
              @clear="clearEventsSelection"
              :key="event_selector"
              data-qa="trigger-event-input"
            />
          </form-item>
        </el-col>

        <el-col :span="4" style="padding-top: 40px">
          <toggle
            v-model="model.event_filter"
            :text="$t('CampaignForm_Select_Event_Filter')"
            data-qa="event-filter-toggle"
          />
        </el-col>

        <el-col :span="12" style="color: #7e8596; padding-top: 40px">
          <i class="icon-info" />
          <span v-if="event_selector === 'inapp'">
            {{ $t('CampaignForm_Events_Event_InappInfo') }}
          </span>
          <span v-if="event_selector === 'server'">
            {{ $t('CampaignForm_Events_Event_ServerInfo') }}
          </span>
        </el-col>
      </el-row>

      <conditions-editor
        ref="conditions-editor"
        v-if="model.event_filter"
        v-model="model.event_conditions"
        data-model-name="Campaign"
        data-model-attribute="event_conditions"
        attribute-list-data-url="/push/event-filter/attribute-list-data"
        :preview="false"
        :no-data-alert="false"
        :single-level="true"
      />
    </div>
  </form-section>
</template>

<script>
import FormSection from '@/common/components/form/FormSection'
import RestApi from '@/core/services/RestApi'
import { projectEndpoint } from '@/core/helpers'
import RadioGroup from '@/common/components/form/RadioGroup'
import Radio from '@/common/components/form/Radio'
import { campaignEventModeEnum } from '@/modules/campaign-manage/enum/CampaignEventModeEnum'
import FormItem from '@/common/components/form/FormItem'
import ConditionsEditor from '@/common/components/ConditionsEditor'
import Toggle from '@/common/components/form/Toggle'

export default {
  name: 'EventSelectorInapp',

  components: {
    Toggle,
    ConditionsEditor,
    FormItem,
    Radio,
    RadioGroup,
    FormSection
  },

  data() {
    return {
      event_selector: this.getEventSelector()
    }
  },

  props: {
    model: {
      type: Object
    }
  },

  computed: {
    sessionStartEnabled() {
      return (
        this.model.event === 'session_start' ||
        ((this.model.inapp_ios || this.model.inapp_android) &&
          !this.model.onsite)
      )
    }
  },

  methods: {
    querySearchEvents(queryString, cb) {
      const params = {
        sort: 'title'
      }

      if (queryString !== null && queryString !== '') {
        params.title = queryString
      }

      let platforms = []
      if (this.event_selector === 'inapp') {
        if (this.model.inapp_ios) {
          platforms.push(1)
        }
        if (this.model.inapp_android) {
          platforms.push(2)
        }
        if (this.model.onsite) {
          platforms.push(3)
        }
        if (!platforms.length) {
          platforms = [1, 2, 3]
        }
      } else if (this.event_selector === 'server') {
        platforms = [0]
      }

      params.source_platforms = platforms.join(',')

      RestApi(
        projectEndpoint({
          method: 'get',
          url: 'events',
          params: params
        })
      ).then(({ data }) => {
        const list = data.map(event => {
          return {
            value: event.title,
            type: event.type
          }
        })
        cb(list)
      })
    },

    onBlur() {
      this.handleSelectEvents({ type: 'custom' })
    },

    handleSelectEvents(selection) {
      this.model.event = selection.type ? selection.type : 'custom'
    },

    clearEventsSelection() {
      this.model.event = null
      this.model.event_title = null
    },

    hidePopover(data) {
      const refRect = data.offsets.reference
      const headerHeight =
        document.querySelector('header > .top-container').offsetHeight || 64

      data.styles.display = refRect.bottom - headerHeight < 0 ? 'none' : 'block'

      return data
    },

    getEventSelector() {
      if (this.model.event_mode === campaignEventModeEnum.SEND) {
        return 'server'
      } else if (this.model.event === 'custom') {
        return 'inapp'
      } else {
        return this.model.event
      }
    }
  },

  watch: {
    event_selector: {
      handler(val) {
        switch (val) {
          case 'server':
            this.model.event = 'custom'
            this.model.event_title = null
            this.model.event_filter = 0
            this.model.event_mode = campaignEventModeEnum.SEND
            break

          case 'inapp':
            this.model.event = 'custom'
            this.model.event_title = null
            this.model.event_filter = 0
            this.model.event_mode = campaignEventModeEnum.SHOW
            break

          case 'session_start':
            this.model.event = 'session_start'
            this.model.event_title = null
            this.model.event_filter = 0
            this.model.event_mode = campaignEventModeEnum.SHOW
            break
        }
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/events/EventSelectorInapp.vue